import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Clock, UserPlus } from "lucide-react";

import Footer from "../Components/Footer";
import DefaultAvatar from "../Components/DefaultAvatar";
import { useAuth } from "../Services/AuthContext";
import { ApiError, formMessage } from "../Services/http";
import {
  acceptFriendInvite,
  fetchFriendInvite,
  type FriendInvitePreview,
} from "../Services/FriendInviteService";
import { clearPendingInvite, rememberPendingInvite } from "../Services/pendingInvite";
import "../Styles/Forms.css";
import "../Styles/InviteAccept.css";

/**
 * Landningssidan för en delad vänbjudan.
 *
 * Sidan är publik med flit. Mottagaren ska få se *vem* som bjuder in innan
 * hen bestämmer sig för att skapa ett konto — en inloggningsruta utan
 * sammanhang är det snabbaste sättet att förlora någon. Backend skickar
 * bara ett visningsnamn till den som är utloggad, så det är också allt
 * sidan kan visa.
 *
 * Är mottagaren utloggad läggs token undan innan hen skickas vidare, och
 * Login och Register plockar upp den efteråt. Se pendingInvite.ts.
 */

type State = "loading" | "ready" | "failed";

const ERROR_TEXT: Record<string, string> = {
  friend_invite_not_found: "That invite link is not valid.",
  friend_invite_expired: "That invite link has expired. Ask for a new one.",
};

export default function InviteAccept() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const [state, setState] = useState<State>("loading");
  const [preview, setPreview] = useState<FriendInvitePreview | null>(null);
  const [error, setError] = useState<string>();
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setState("failed");
      setError("That invite link is incomplete.");
      return;
    }

    setState("loading");

    try {
      setPreview(await fetchFriendInvite(token));
      setState("ready");
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      setError(
        (code && ERROR_TEXT[code]) ?? formMessage(e) ?? "That invite link is not valid."
      );
      setState("failed");
    }
  }, [token]);

  // Laddas om när inloggningen ändras: samma länk svarar med mer när
  // mottagaren väl har ett konto.
  useEffect(() => {
    if (isAuthLoading) return;
    load();
  }, [load, isAuthLoading, isAuthenticated]);

  // En trasig eller utgången länk ska inte ligga kvar och vänta på nästa
  // inloggning.
  useEffect(() => {
    if (state === "failed") clearPendingInvite();
  }, [state]);

  const goAuthenticate = (path: "/login" | "/register") => {
    rememberPendingInvite(token);
    navigate(path);
  };

  const handleAccept = async () => {
    setIsAccepting(true);

    try {
      const result = await acceptFriendInvite(token);

      setAccepted(true);
      setPreview((current) =>
        current
          ? {
              ...current,
              viewer: {
                ...current.viewer,
                followState: result.followState,
                followsYou: result.followsYou,
                connection: result.connection,
              },
            }
          : current
      );
      clearPendingInvite();
    } catch (e) {
      setError(formMessage(e) ?? "Could not send that request. Try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  if (state === "loading" || isAuthLoading) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <p role="status">Loading invite…</p>
        </div>
        <Footer />
      </main>
    );
  }

  if (state === "failed" || !preview) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1>That invite did not work</h1>
          <p role="alert">{error}</p>
          <Link className="form-link" to="/">
            Go to Beatspot
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const { from, viewer } = preview;

  return (
    <main className="auth-page">
      <div className="auth-card invite-card">
        <div className="invite-from">
          <DefaultAvatar avatarUrl={from.avatarUrl ?? undefined} name={from.displayName} />
          <h1>
            <strong>{from.displayName}</strong> invited you to Beatspot
          </h1>
        </div>

        {/* Utloggad: namnet, och sedan vägen in. Inget om profilen, inget
            om var någon befinner sig. */}
        {!viewer.authenticated && (
          <>
            <p>
              Create an account or sign in, and we'll send {from.displayName} a follow
              request from you.
            </p>

            <button
              type="button"
              className="follow-button invite-primary"
              onClick={() => goAuthenticate("/register")}
            >
              Create account
            </button>

            <button
              type="button"
              className="form-link invite-secondary"
              onClick={() => goAuthenticate("/login")}
            >
              I already have an account
            </button>
          </>
        )}

        {viewer.authenticated && viewer.isSelf && (
          <>
            <p>This is your own invite link — share it with someone else.</p>
            <Link className="form-link" to="/profile">
              Go to your profile
            </Link>
          </>
        )}

        {viewer.authenticated && !viewer.isSelf && viewer.connection === "connected" && (
          <>
            <p className="invite-status">
              <Check size={16} strokeWidth={2.2} />
              You two are already connected.
            </p>
            <Link className="form-link" to={from.id ? `/profile/${from.id}` : "/feed"}>
              Go to {from.displayName}'s profile
            </Link>
          </>
        )}

        {viewer.authenticated && !viewer.isSelf && viewer.connection !== "connected" && (
          <>
            {accepted ? (
              <>
                <p className="invite-status">
                  <Clock size={16} strokeWidth={2.2} />
                  Request sent. {from.displayName} decides who gets to follow along.
                </p>
                <Link className="form-link" to={from.id ? `/profile/${from.id}` : "/feed"}>
                  Go to {from.displayName}'s profile
                </Link>
              </>
            ) : (
              <>
                <p>
                  We'll send a follow request. {from.displayName} still has to accept it
                  before either of you sees the other's check-ins.
                </p>

                <button
                  type="button"
                  className="follow-button invite-primary"
                  onClick={handleAccept}
                  disabled={isAccepting}
                >
                  <UserPlus size={15} strokeWidth={2.2} />
                  {isAccepting ? "Sending…" : `Connect with ${from.displayName}`}
                </button>

                {error && <p role="alert" className="form-error">{error}</p>}
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
